import React, { useRef, useState } from "react";
import { Box } from "@mui/material";
import { styles } from "./ask-user-form";
import { FIELD_TYPES, StriimButton, StriimFormBuilder, StriimTypography } from "@striim/striim-ui";
import { buildCancelledResponse } from "../utils/hitl-cancel";

export interface AskUserConfirmationArgs {
    /** Optional heading shown above the message. */
    title?: string;
    /** The text the user is asked to confirm. */
    message: string;
    /** Label for the confirm button (default "Confirm"). */
    confirmLabel?: string;
    /** Label for the cancel button (default "Cancel"). */
    cancelLabel?: string;
}

export interface AskUserConfirmationProps {
    args: AskUserConfirmationArgs;
    respond?: (result: string) => void;
}

/**
 * Builds the StriimFormBuilder field list for a confirmation: an optional title, a message, and a
 * Confirm / Cancel pair. Confirm/Cancel are wired directly to the callbacks so clicking either one
 * reports the decision back to the agent immediately — there are no form values to collect.
 */
const buildStriimFormComponents = (args: AskUserConfirmationArgs, onConfirm: () => void, onCancel: () => void) => {
    const components = [];

    // Title
    if (args.title) {
        components.push({
            name: "title",
            type: FIELD_TYPES.COMPONENT,
            component: <StriimTypography variant="h3">{args.title}</StriimTypography>
        });
    }
    //Message
    if (args.message) {
        components.push({
            name: "message",
            type: FIELD_TYPES.COMPONENT,
            component: (
                <StriimTypography variant="body4" color="greyscale.800">
                    {args.message}
                </StriimTypography>
            )
        });
    }
    // Confirmation Button
    components.push({
        name: "confirmButton",
        type: FIELD_TYPES.COMPONENT,
        component: (
            <StriimButton fullWidth variant="primary" onClick={onConfirm}>
                {args.confirmLabel ?? "Confirm"}
            </StriimButton>
        )
    });

    // Cancel button
    components.push({
        name: "cancelButton",
        type: FIELD_TYPES.COMPONENT,
        component: (
            <StriimButton fullWidth variant="secondary" onClick={onCancel}>
                {args.cancelLabel ?? "Cancel"}
            </StriimButton>
        )
    });
    return components;
};

/**
 * Confirmation variant of `AskUserForm`. Renders a message with Confirm / Cancel buttons via
 * `StriimFormBuilder` and returns the decision to the agent — `{ "confirmed": true }` when the
 * user confirms, `{ "confirmed": false }` when they cancel.
 */
export const AskUserConfirmation: React.FC<AskUserConfirmationProps> = ({ args, respond }) => {
    const [answered, setAnswered] = useState(false);
    const ref = useRef();

    const handleConfirm = () => {
        if (answered) return;
        setAnswered(true);
        respond?.(JSON.stringify({ confirmed: true }));
    };

    const handleCancel = () => {
        if (answered) return;
        setAnswered(true);
        respond?.(buildCancelledResponse({ confirmed: false }));
    };

    const striimFormData = buildStriimFormComponents(args, handleConfirm, handleCancel);

    return (
        <Box sx={styles.container}>
            <StriimFormBuilder formRef={ref} data={striimFormData}></StriimFormBuilder>
        </Box>
    );
};
